import { useEffect, useMemo, useState, useRef } from "react";
import { CalendarRange, ChevronLeft, ChevronRight, Search, Loader2, AlertTriangle, Phone } from "lucide-react";
import { api } from "../../lib/api";
import { toast } from "sonner";

const dayLabel = (iso) => {
  const d = new Date(iso);
  const wd = ["DOM","LUN","MAR","MER","GIO","VEN","SAB"][d.getDay()];
  return { wd, day: d.getDate(), month: d.toLocaleString("it-IT", { month: "short" }) };
};

const COLORS = [
  "from-rose-500/80 to-rose-700/80",
  "from-emerald-500/80 to-emerald-700/80",
  "from-amber-500/80 to-amber-700/80",
  "from-sky-500/80 to-sky-700/80",
  "from-fuchsia-500/80 to-fuchsia-700/80",
  "from-indigo-500/80 to-indigo-700/80",
];

const hashColor = (s) => {
  let h = 0;
  for (let i = 0; i < (s || "").length; i++) h = ((h << 5) - h) + s.charCodeAt(i);
  return COLORS[Math.abs(h) % COLORS.length];
};

export default function Planning() {
  const today = new Date().toISOString().slice(0, 10);
  const [from, setFrom] = useState(today);
  const [days, setDays] = useState(30);
  const [data, setData] = useState({ days: [], cars: [], bookings: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [dragging, setDragging] = useState(null);  // {bookingId, originalStart}
  const [busy, setBusy] = useState(false);

  const to = useMemo(() => {
    const d = new Date(from);
    d.setDate(d.getDate() + days - 1);
    return d.toISOString().slice(0, 10);
  }, [from, days]);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/planning?from_date=${from}&to_date=${to}`);
      setData(r.data || { days: [], cars: [], bookings: [] });
    } catch (e) { toast.error("Errore: " + e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [from, days]);

  const filteredCars = useMemo(() => {
    const s = (search || "").toLowerCase().trim();
    if (!s) return data.cars;
    return data.cars.filter((c) => `${c.marca} ${c.modello} ${c.targa}`.toLowerCase().includes(s));
  }, [data.cars, search]);

  const shiftWeek = (dir) => {
    const d = new Date(from);
    d.setDate(d.getDate() + dir * 7);
    setFrom(d.toISOString().slice(0, 10));
  };

  const bookingsByCarDay = useMemo(() => {
    const idx = {}; // {car_id: [{...booking, startIdx, endIdx}]}
    for (const b of data.bookings) {
      const sIdx = data.days.indexOf(b.data_inizio);
      const eIdx = data.days.indexOf(b.data_fine);
      const startIdx = sIdx >= 0 ? sIdx : 0;
      const endIdx = eIdx >= 0 ? eIdx : data.days.length - 1;
      if (!idx[b.car_id]) idx[b.car_id] = [];
      idx[b.car_id].push({ ...b, startIdx, endIdx });
    }
    return idx;
  }, [data]);

  const onDrop = async (booking, newCarId, newStartDay) => {
    setBusy(true);
    try {
      const duration = booking.endIdx - booking.startIdx;
      const newEnd = new Date(newStartDay);
      newEnd.setDate(newEnd.getDate() + duration);
      const payload = {
        data_inizio: newStartDay,
        data_fine: newEnd.toISOString().slice(0, 10),
        ...(newCarId !== booking.car_id ? { car_id: newCarId } : {}),
      };
      await api.patch(`/contracts/${booking.id}/reschedule`, payload);
      toast.success(`Contratto ${booking.contract_num || booking.id.slice(0,8)} spostato`);
      load();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error("Errore: " + msg);
    } finally { setBusy(false); setDragging(null); }
  };

  return (
    <div className="space-y-5" data-testid="planning-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-black uppercase tracking-tight flex items-center gap-3">
            <CalendarRange className="text-[#ff0033]" size={26} />
            Planning Flotta
          </h1>
          <p className="text-sm text-white/45 mt-1">Tabellone Gantt drag-and-drop · sposta contratti tra veicoli · conflict detection live</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button onClick={() => shiftWeek(-1)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08]" data-testid="prev-week"><ChevronLeft size={16} /></button>
          <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-1.5 text-sm" data-testid="from-date" />
          <select value={days} onChange={(e) => setDays(+e.target.value)} className="bg-white/[0.03] border border-white/10 rounded-lg px-3 py-1.5 text-sm">
            <option value={14}>14gg</option>
            <option value={30}>30gg</option>
            <option value={60}>60gg</option>
            <option value={90}>90gg</option>
          </select>
          <button onClick={() => shiftWeek(1)} className="p-2 rounded-lg bg-white/[0.04] hover:bg-white/[0.08]" data-testid="next-week"><ChevronRight size={16} /></button>
          <button onClick={() => setFrom(today)} className="px-3 py-1.5 rounded-lg text-xs bg-[#ff0033]/15 border border-[#ff0033]/40 hover:bg-[#ff0033]/25">Oggi</button>
        </div>
      </div>

      <div className="relative max-w-sm">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Filtra veicoli (marca, modello, targa)…"
          data-testid="planning-search"
          className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-9 pr-4 py-2 text-sm" />
      </div>

      <div className="glass rounded-2xl overflow-hidden">
        {loading && <div className="text-center py-10 text-white/40">Caricamento…</div>}
        {!loading && (
          <div className="overflow-x-auto">
            <div className="min-w-max">
              {/* HEADER GIORNI */}
              <div className="flex sticky top-0 z-10 bg-[#0b0b0d] border-b border-white/10">
                <div className="w-56 shrink-0 p-2 text-[10px] uppercase tracking-widest text-white/40 border-r border-white/5">Veicolo</div>
                <div className="flex">
                  {data.days.map((d) => {
                    const dl = dayLabel(d);
                    const isWeekend = ["DOM", "SAB"].includes(dl.wd);
                    const isToday = d === today;
                    return (
                      <div key={d} className={`w-14 shrink-0 p-1 text-center border-r border-white/5 ${isWeekend ? "bg-white/[0.02]" : ""} ${isToday ? "bg-[#ff0033]/15" : ""}`}>
                        <div className={`text-[9px] uppercase ${isWeekend ? "text-amber-300" : "text-white/40"}`}>{dl.wd}</div>
                        <div className="text-xs font-bold">{dl.day}</div>
                        <div className="text-[8px] text-white/30">{dl.month}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* RIGHE VEICOLI */}
              {filteredCars.map((car) => (
                <div key={car.id} className="flex border-b border-white/5 hover:bg-white/[0.01]" data-testid={`row-${car.targa}`}>
                  <div className="w-56 shrink-0 p-2 border-r border-white/5 flex items-center gap-2">
                    {car.foto?.[0] ? (
                      <img src={car.foto[0]} alt="" className="w-10 h-10 object-cover rounded-md" onError={(e) => { e.target.style.display = "none"; }} />
                    ) : <div className="w-10 h-10 bg-white/5 rounded-md" />}
                    <div className="min-w-0">
                      <div className="text-xs font-semibold truncate">{car.marca} {car.modello}</div>
                      <div className="text-[10px] font-mono text-white/50">{car.targa}</div>
                    </div>
                  </div>
                  <div className="flex relative">
                    {data.days.map((d, idx) => {
                      const isWeekend = ["dom", "sab"].includes(dayLabel(d).wd.toLowerCase());
                      return (
                        <div key={d}
                          onDragOver={(e) => { e.preventDefault(); e.currentTarget.style.background = "rgba(255,0,51,0.15)"; }}
                          onDragLeave={(e) => { e.currentTarget.style.background = ""; }}
                          onDrop={(e) => {
                            e.currentTarget.style.background = "";
                            if (!dragging) return;
                            onDrop(dragging, car.id, d);
                          }}
                          className={`w-14 h-14 shrink-0 border-r border-white/5 ${isWeekend ? "bg-white/[0.015]" : ""}`}
                          data-day={d} />
                      );
                    })}
                    {/* Contratti */}
                    {(bookingsByCarDay[car.id] || []).map((b) => {
                      const width = (b.endIdx - b.startIdx + 1) * 56 - 4;
                      const left = b.startIdx * 56 + 2;
                      return (
                        <div key={b.id}
                          draggable
                          onDragStart={() => setDragging(b)}
                          onDragEnd={() => setDragging(null)}
                          className={`absolute top-1.5 h-11 bg-gradient-to-r ${hashColor(b.customer)} rounded-md p-1.5 cursor-grab active:cursor-grabbing shadow-lg border border-white/20 hover:scale-[1.02] hover:z-20 transition group`}
                          style={{ left, width }}
                          data-testid={`booking-${b.id}`}
                          title={`${b.customer} · ${b.data_inizio} → ${b.data_fine} · €${b.totale}`}>
                          <div className="text-[10px] font-bold uppercase tracking-wider truncate text-white">{b.customer}</div>
                          <div className="text-[9px] truncate text-white/90 flex items-center gap-1">
                            €{b.totale} {b.telefono && <Phone size={8} />}
                          </div>
                          {!b.pagato && (
                            <span className="absolute top-1 right-1 w-1.5 h-1.5 bg-amber-400 rounded-full" title="Acconto solo" />
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {busy && <div className="fixed bottom-6 right-6 glass-strong rounded-full px-4 py-2 flex items-center gap-2 text-xs"><Loader2 size={14} className="animate-spin text-[#ff0033]" /> Spostamento contratto…</div>}

      <div className="text-[11px] text-white/40 flex flex-wrap gap-4">
        <span>🟢 Trascina un blocco contratto su un altro veicolo o giorno per riallocarlo</span>
        <span><AlertTriangle size={11} className="inline text-amber-300" /> Conflict detection automatica al drop</span>
      </div>
    </div>
  );
}
